var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { convertToNumber3, DeleteModelMessage, DisableSelectionHighlight, PickupFacet, InteractionFacet, InteractionFacetActivationMessage, ModuleId, MulticastDelegate, PerformActionsMessage, PerformServerActionMessage, RemoveFacetFromModelMessage, RemoveModelMessage, RemoveSubModelMessage, SelectEventMessage, SelectionHighlightFacet, UpdateFacetOnModelMessage, worldToLocalPosition } from "flavr-common";
import { Quaternion, Vector3 } from "flavr-math";
import { EndSessionMessage } from "@messages/session";
import { ControllerConnectedMessage, ControllerDisconnectedMessage, ControllerMovedMessage, ControllerTrackingChangedMessage } from "@messages/controller/ControllerMessages";
import { ButtonSignal, ButtonType, ControllerButtonMessage } from "flavr-common";
import { ResetControllerButtonDescription, SetControllerButtonDescription } from "@messages/controller/ControllerMessages";
import { ModelClickedMessage, NoModelClickedMessage, RequestModelSelectionMessage, ResetControllerButtonActionsMessage, SetAvailableSpeechCommandsMessage, SetControllerButtonActionsMessage } from "@messages/interaction/InteractionMessages";
import { SendMessagesInteractionHandler } from "@messages/interaction/SendMessages";
import { ModelSelectMessage } from "@messages/selection/SelectionStrategyMessages";
import { STTCommandMessage } from "@messages/stt/STTMessages";
import { PickupInteractionHandler } from "@modules/interaction/handlers/modelManipulation/PickupInteractionHandler";
import { SelectionHandler } from "@modules/selection/SelectionHandler";
import { CommandToMessageFactory } from "@modules/stt";
import { modelPool } from "@modules/model";
import { FacetHandler, FacetRemoveHandler, MessageHandler, ModelEventHandler, ModelEventType, NewModule } from "@modules/module";
import { ShowTooltipsInteractionHandler } from "./handlers/ShowTooltipsInteractionHandler";
import { RoomInteractionHandler } from "./handlers/RoomInteractionHandler";
import { GrabbableInteractionHandler } from "./handlers/modelManipulation/GrabbableInteractionHandler";
import { ForwardInteractionHandler } from "./handlers/ForwardInteractionHandler";
import { FlyToHitpointInteractionHandler } from "./handlers/FlyToHitpointInteractionHandler";
import { CreateLabelSpeechInteractionHandler } from "./handlers/CreateLabelInteractionHandler";
import { ModifierModelInteractionHandler } from "./handlers/ModifierModelInteractionHandler";
import { LineConnectionInteractionHandler } from "./handlers/LineConnectionInteractionHandler";
import { InteractionMetaDataManager } from "./InteractionMetaDataManager";
import { ReferencePlaneInteractionHandler } from "./handlers/ReferencePlaneInteractionHandler";
import { HighlightAdditionalModelsInteractionHandler } from "./handlers/highlight/HighlightAdditionalModelsInteractionHandler";
import { HighlightLineStripInteractionHandler } from "./handlers/highlight/HighlightLineStripInteractionHandler";
import { SliderInteractionHandler } from "./handlers/slider/SliderInteractionHandler";
import { UI3DInteractionHandler } from "./handlers/UI3DInteractionHandler";
import { ModelTransformInteractionHandler } from "./handlers/ModelTransformInteractionHandler";
import { PickupAction } from "./handlers/modelManipulation/PickupAction";
import { ToolBeltPickupInteractionHandler } from "./handlers/modelManipulation/ToolBeltPickupInteractionHandler";
import { xrService } from "@modules/xr/XRService";
/**
 * Coordinates model interactions so controller input, speech commands and interaction facets are converted into context-sensitive actions, highlights and selection feedback.
 *
 * Facets:
 * - `InteractionFacet` - Caches the behaviors/actions that make a model interactive and, when added, ensures a `SelectionHighlightFacet` exists for it; removing it clears the cached interaction and any highlight facet this module auto-created.
 * - `DisableSelectionHighlight` and `SelectionHighlightFacet` - Control whether a model should receive automatic selection highlighting and how that highlight should look; changes are reflected immediately for controllers currently selecting the model.
 * - `PickupFacet` - Shorthand for making a model grabbable: builds an `InteractionFacet` with a pickup on-click action from the facet's offset/clone/drop-off settings and applies it to the model.
 *
 * Messages:
 * - `ControllerButtonMessage`, `ControllerMovedMessage`, `ControllerConnectedMessage`, `ControllerDisconnectedMessage` and `ControllerTrackingChangedMessage` - Provide the controller input and tracking state that drives interaction handlers.
 * - `RequestModelSelectionMessage`, `PerformActionsMessage`, `SetControllerButtonActionsMessage`, `SetAvailableSpeechCommandsMessage` and `STTCommandMessage` - Update interaction context, trigger actions and connect speech input to interaction handlers.
 * - `InteractionFacetActivationMessage`, `ResetControllerButtonActionsMessage`, `DeleteModelMessage`, `RemoveModelMessage`, `RemoveSubModelMessage` and `EndSessionMessage` - Enable or disable interaction state and clean up tracked interaction data.
 *
 * Events:
 * - `ModelEventType.TRANSFORM_UPDATED` - Keeps cached world transforms in sync for interactions that depend on current model positions.
 * - `ModelEventType.MODEL_DESTROYED` - Removes interaction state for models that are no longer available.
 */
export class InteractionModule extends NewModule {
    /**
     * Wires up the `SelectionHandler` so raw selection/deselection events are routed to this module's `onSelect` and
     * `onDeselect`, and creates the metadata manager used by handlers that need per-model interaction metadata.
     */
    constructor() {
        super(ModuleId.INTERACTION);
        this.behaviorHandler = new Map();
        this.actionHandler = new Map();
        this.speechCommandHandler = new Map();
        this.modelsWithContextMenuFacets = new Set();
        this.disableSelectionHighlight = new Map();
        this.selectionHighlightEnabled = new Map();
        this.selectionHighlightColor = new Map();
        this.autoCreatedSelectionHighlight = new Set();
        this.pendingAutoCreatedSelectionHighlight = new Set();
        this.interactions = new Map();
        this.pausedInteractions = new Map();
        this.controllerButtonActions = new Map();
        this.availableSpeechCommands = [];
        this.modelTransforms = new Map();
        this.controller = new Map();
        this.controllerHandTracking = new Map();
        this.commandFactory = new CommandToMessageFactory();
        this.worldTransformEvent = new Map();
        this.locationHref = "";
        this.onSelectNothingHit = new MulticastDelegate();
        this.selectionHandler = new SelectionHandler(this.connection, (model, controllerUid, hitPoint) => {
            const controller = this.controller.get(controllerUid);
            (controller !== undefined) && this.onSelect(model, controller, hitPoint);
        }, (model, controllerUid, hitPoint) => {
            const controller = this.controller.get(controllerUid);
            (controller !== undefined) && this.onDeselect(model, controller, hitPoint);
        });
        this.metaDataManager = new InteractionMetaDataManager(this.connection);
    }
    /// Getter and setter ///
    /**
     * Returns the last cached world transform recorded for the model, if the module has seen a transform update for it.
     */
    getModelTransformation(uid) {
        return this.modelTransforms.get(uid);
    }
    /**
     * Returns the interaction handler registered for the given behavior/action/speech-command name, if any.
     */
    getInteractionHandler(interactionType) {
        return this.behaviorHandler.get(interactionType);
    }
    /**
     * Returns the description to show for a model's current interaction: the interaction facet's explicit description
     * if set, otherwise the first description supplied by one of its behavior, click or double-click action handlers.
     */
    getInteractionDescription(uid) {
        const interaction = this.interactions.get(uid);
        if (interaction === undefined)
            return "";
        if (interaction.facet.description !== undefined)
            return interaction.facet.description;
        for (const behavior of interaction.facet.behaviors) {
            const handler = this.behaviorHandler.get(behavior.behavior);
            if (handler !== undefined) {
                const description = handler.getBehaviorDescription(behavior, uid);
                if (description !== undefined)
                    return description;
            }
        }
        for (const action of interaction.facet.onClick) {
            const handler = this.actionHandler.get(action.action);
            if (handler !== undefined) {
                const description = handler.getActionDescription(action, uid);
                if (description !== undefined)
                    return description;
            }
        }
        for (const action of interaction.facet.onDoubleClick) {
            const handler = this.actionHandler.get(action.action);
            if (handler !== undefined) {
                const description = handler.getActionDescription(action, uid);
                if (description !== undefined)
                    return description;
            }
        }
        return "";
    }
    /**
     * Registers a callback that is invoked with the model's world position/orientation/scale whenever its cached world
     * transform is updated, keyed by `id` so it can later be removed with `unsubscribeWorldTransformEvent`.
     */
    subscribeWorldTransformEvent(uid, id, callback) {
        let model = this.worldTransformEvent.get(uid);
        if (model === undefined) {
            this.worldTransformEvent.set(uid, (model = new Map()));
        }
        model.set(id, callback);
    }
    /**
     * Removes a previously registered world-transform callback for the model, cleaning up the per-model entry once no
     * callbacks remain.
     */
    unsubscribeWorldTransformEvent(uid, id) {
        const model = this.worldTransformEvent.get(uid);
        if (model !== undefined) {
            model.delete(id);
            if (model.size === 0) {
                this.worldTransformEvent.delete(uid);
            }
        }
    }
    /**
     * Registers an interaction handler for every behavior, action and speech command name it declares, so those names
     * can later be looked up via `getInteractionHandler` or dispatched to during interaction handling.
     */
    registerHandler(handler) {
        for (const names of handler.behaviorNames) {
            this.behaviorHandler.set(names, handler);
        }
        for (const names of handler.actionNames) {
            this.actionHandler.set(names, handler);
        }
        for (const names of handler.speechCommandNames) {
            this.speechCommandHandler.set(names, handler);
        }
    }
    /**
     * Locks or unlocks selection changes for the given controller, delegating to the selection handler.
     */
    lockSelection(controllerUid, lock) {
        this.selectionHandler.lockSelection(controllerUid, lock);
    }
    /// Life-cycle methods ///
    /**
     * Registers all of the module's built-in interaction handlers (tooltips, room interaction, grabbing/pickup,
     * forwarding, speech-driven labels, modifiers, transforms, line connections, fly-to, reference planes, highlighting,
     * sliders and UI3D) and resets the per-controller button actions.
     */
    onInit(locationHref) {
        this.locationHref = locationHref;
        this.registerHandler(new ShowTooltipsInteractionHandler());
        this.registerHandler(new RoomInteractionHandler());
        this.registerHandler(new GrabbableInteractionHandler(this));
        this.registerHandler(new PickupInteractionHandler());
        this.registerHandler(new ToolBeltPickupInteractionHandler());
        this.registerHandler(new ForwardInteractionHandler(this));
        this.registerHandler(new SendMessagesInteractionHandler(locationHref));
        this.registerHandler(new CreateLabelSpeechInteractionHandler(this));
        this.registerHandler(new ModifierModelInteractionHandler());
        this.registerHandler(new ModelTransformInteractionHandler(this));
        this.registerHandler(new LineConnectionInteractionHandler());
        this.registerHandler(new FlyToHitpointInteractionHandler());
        this.registerHandler(new ReferencePlaneInteractionHandler(this.metaDataManager, locationHref));
        this.registerHandler(new HighlightAdditionalModelsInteractionHandler());
        this.registerHandler(new HighlightLineStripInteractionHandler(this.metaDataManager));
        this.registerHandler(new SliderInteractionHandler(this));
        this.registerHandler(new UI3DInteractionHandler(this));
        this.resetControllerButtonActions();
    }
    /// Controller button events ///
    /**
     * Marks a model as selected by the given controller: broadcasts the selection, shows the selection highlight if it
     * should be visible, updates the controller's select-button description to the interaction's description, and
     * notifies the model's behavior handlers.
     */
    onSelect(uid, controller, hitPoint) {
        this.connection.send(new ModelSelectMessage(controller.uid, uid, true));
        if (this.shouldShowSelectionHighlight(uid)) {
            this.sendSelectionHighlightEvent(controller.uid, uid, true);
        }
        const interaction = this.interactions.get(uid);
        if (interaction === undefined)
            return;
        const description = this.getInteractionDescription(uid);
        this.connection.send(new SetControllerButtonDescription(ButtonType.SELECT, description, controller.uid));
        this.forBehaviorOfModel(uid, (param, handler) => {
            handler.onSelect(param, uid, controller, hitPoint);
        });
    }
    /**
     * Marks a model as deselected by the given controller: broadcasts the deselection, resets the controller's select
     * and discard button descriptions, hides the selection highlight if it was shown, and notifies the model's
     * behavior handlers.
     */
    onDeselect(uid, controller, hitPoint) {
        this.connection.send(new ModelSelectMessage(controller.uid, uid, false));
        this.connection.send(new ResetControllerButtonDescription(controller.uid, ButtonType.SELECT));
        this.connection.send(new ResetControllerButtonDescription(controller.uid, ButtonType.DISCARD));
        if (this.shouldShowSelectionHighlight(uid)) {
            this.sendSelectionHighlightEvent(controller.uid, uid, false);
        }
        this.forBehaviorOfModel(uid, (param, handler) => {
            handler.onDeselect(param, uid, controller, hitPoint);
        });
    }
    /**
     * Forwards a select-button press on the given model to the behavior handlers registered for its interaction.
     */
    onSelectButtonPress(uid, controller, hitPoint) {
        this.forBehaviorOfModel(uid, (param, handler) => {
            handler.onSelectButtonPress(param, uid, controller, hitPoint);
        });
    }
    /**
     * Forwards a select-button release on the given model to the behavior handlers registered for its interaction.
     */
    onSelectButtonRelease(uid, controller, hitPoint) {
        this.forBehaviorOfModel(uid, (param, handler) => {
            handler.onSelectButtonRelease(param, uid, controller, hitPoint);
        });
    }
    /**
     * Runs the model's `onClick` actions, forwards the click to its behavior handlers, and broadcasts a
     * `ModelClickedMessage` with the hit point in both world and model-local space.
     */
    onSelectButtonClick(uid, controller, hitPoint) {
        const interaction = this.interactions.get(uid);
        if ((interaction === null || interaction === void 0 ? void 0 : interaction.facet.onClick) !== undefined) {
            void this.performActions(interaction.facet.onClick, {
                targetUid: uid,
                sourceUid: controller.uid,
                position: hitPoint,
                controller
            });
        }
        this.forBehaviorOfModel(uid, (param, handler) => {
            handler.onSelectButtonClick(param, uid, controller, hitPoint);
        });
        const model = modelPool.getModel(uid);
        const localPoint = (model !== undefined)
            ? worldToLocalPosition(new Vector3().fromArray(hitPoint), model.getWorldTransform())
            : new Vector3();
        this.connection.send(new ModelClickedMessage(uid, controller.uid, hitPoint, convertToNumber3(localPoint)));
    }
    /**
     * Runs the model's `onDoubleClick` actions and forwards the double-click to its behavior handlers.
     */
    onSelectButtonDoubleClick(uid, controller, hitPoint) {
        return __awaiter(this, void 0, void 0, function* () {
            const interaction = this.interactions.get(uid);
            if ((interaction === null || interaction === void 0 ? void 0 : interaction.facet.onDoubleClick) !== undefined) {
                yield this.performActions(interaction.facet.onDoubleClick, {
                    targetUid: uid,
                    sourceUid: controller.uid,
                    position: hitPoint,
                    controller
                });
            }
            this.forBehaviorOfModel(uid, (param, handler) => {
                handler.onSelectButtonDoubleClick(param, uid, controller, hitPoint);
            });
        });
    }
    /**
     * Forwards a select-button hold on the given model to the behavior handlers registered for its interaction.
     */
    onSelectButtonHold(uid, controller, hitPoint) {
        this.forBehaviorOfModel(uid, (param, handler) => {
            handler.onSelectButtonHold(param, uid, controller, hitPoint);
        });
    }
    /**
     * Forwards the release of a held select-button on the given model to the behavior handlers registered for its
     * interaction.
     */
    onSelectButtonHoldReleased(uid, controller, hitPoint) {
        this.forBehaviorOfModel(uid, (param, handler) => {
            handler.onSelectButtonHoldReleased(param, uid, controller, hitPoint);
        });
    }
    /// Controller move events ///
    /**
     * Forwards a controller move to the behavior handlers registered for the model currently selected by that
     * controller.
     */
    onControllerMove(uid, controller, hitPoint) {
        this.forBehaviorOfModel(uid, (param, handler) => {
            handler.onControllerMove(param, uid, controller, hitPoint);
        });
    }
    /// Message handlers ///
    onReset() {
        this.behaviorHandler.clear();
        this.actionHandler.clear();
        this.speechCommandHandler.clear();
        this.disableSelectionHighlight.clear();
        this.selectionHighlightEnabled.clear();
        this.selectionHighlightColor.clear();
        this.autoCreatedSelectionHighlight.clear();
        this.pendingAutoCreatedSelectionHighlight.clear();
        this.interactions.clear();
        this.availableSpeechCommands = [];
        this.modelTransforms.clear();
        this.worldTransformEvent.clear();
        this.onInit(this.locationHref);
    }
    resetControllerButtonActions() {
        this.controllerButtonActions.clear();
    }
    handleModelTransformUpdateMessage(model, _localTransform) {
        let modelTransform = this.modelTransforms.get(model.getUID());
        if (modelTransform === undefined) {
            modelTransform = {
                pos: new Vector3(),
                ori: new Quaternion(),
                scale: new Vector3()
            };
            this.modelTransforms.set(model.getUID(), modelTransform);
        }
        modelTransform.pos = model.getWorldPosition();
        modelTransform.ori = model.getWorldOrientation();
        modelTransform.scale = model.getWorldScale();
        const worldTransformCallbacks = this.worldTransformEvent.get(model.getUID());
        if (worldTransformCallbacks !== undefined) {
            for (const cb of worldTransformCallbacks.values()) {
                cb(modelTransform.pos, modelTransform.ori, modelTransform.scale);
            }
        }
    }
    handleInteractionFacetActivationMessage(msg) {
        if (msg.enabled) {
            const pausedInteraction = this.pausedInteractions.get(msg.targetUid);
            if (pausedInteraction !== undefined) {
                this.interactions.set(msg.targetUid, pausedInteraction);
                this.pausedInteractions.delete(msg.targetUid);
            }
        }
        else {
            const interaction = this.interactions.get(msg.targetUid);
            if (interaction !== undefined) {
                this.pausedInteractions.set(msg.targetUid, interaction);
                this.interactions.delete(msg.targetUid);
            }
        }
    }
    handleControllerButtonMessage(msg) {
        // We only react to some of the button types
        if (![ButtonType.SELECT, ButtonType.GRAB, ButtonType.DISCARD, ButtonType.CLICK].includes(msg.buttonType))
            return;
        if (msg.buttonType === ButtonType.SELECT) {
            switch (msg.input) {
                case ButtonSignal.PRESSED:
                    this.handleControllerSelectButtonPress(msg.uid);
                    break;
                case ButtonSignal.RELEASED:
                    this.handleControllerSelectButtonRelease(msg.uid);
                    break;
                case ButtonSignal.CLICKED:
                    this.handleControllerSelectButtonClick(msg.uid);
                    break;
                case ButtonSignal.DOUBLE_CLICK:
                    this.handleControllerSelectButtonDoubleClick(msg.uid);
                    break;
                case ButtonSignal.HOLD:
                    this.handleControllerSelectButtonHold(msg.uid);
                    break;
                case ButtonSignal.HOLD_RELEASED:
                    this.handleControllerSelectButtonHoldReleased(msg.uid);
                    break;
            }
        }
        const action = this.controllerButtonActions.get(msg.buttonType);
        const controller = this.controller.get(msg.uid);
        if (action !== undefined && controller !== undefined) {
            const selection = this.selectionHandler.getSelection(msg.uid);
            const context = {
                targetUid: (selection !== undefined) ? selection.uid : undefined,
                sourceUid: msg.uid,
                position: (selection !== undefined) ? selection.hitPoint : convertToNumber3(controller.position),
                controller
            };
            switch (msg.input) {
                case ButtonSignal.CLICKED:
                    void this.performActions(action.onClick, context);
                    break;
                case ButtonSignal.DOUBLE_CLICK:
                    void this.performActions(action.onDoubleClick, context);
                    break;
                case ButtonSignal.HOLD:
                    void this.performActions(action.onHold, context);
                    break;
                case ButtonSignal.HOLD_RELEASED:
                    void this.performActions(action.onHoldReleased, context);
                    break;
            }
        }
    }
    handleRequestModelSelectionMessage(msg) {
        if (msg.uid === undefined) {
            this.onSelectNothingHit.invoke();
            this.connection.send(new NoModelClickedMessage());
            return;
        }
        const interaction = this.interactions.get(msg.uid);
        if ((interaction === null || interaction === void 0 ? void 0 : interaction.facet.onClick) != null) {
            void this.performActions(interaction.facet.onClick, {
                targetUid: msg.uid,
                position: [0, 0, 0]
            });
        }
    }
    handleControllerMovedMessage(msg) {
        let controller = this.controller.get(msg.uid);
        if (controller === undefined) {
            this.controller.set(msg.uid, (controller = {
                uid: msg.uid,
                position: new Vector3(),
                orientation: new Quaternion(),
                world_scale: 1
            }));
        }
        controller.position.fromArray(msg.position);
        controller.orientation.fromArray(msg.orientation);
        controller.world_scale = msg.scale;
        const selection = this.selectionHandler.getSelection(msg.uid);
        if (selection !== undefined) {
            this.onControllerMove(selection.uid, controller, selection.hitPoint);
        }
    }
    handleControllerConnectedMessage(msg) {
        this.controllerHandTracking.set(msg.uid, msg.hasTrackedHand);
    }
    handleControllerTrackingChangedMessage(msg) {
        this.controllerHandTracking.set(msg.uid, msg.hasTrackedHand);
    }
    handleControllerDisconnectedMessage(msg) {
        this.controllerHandTracking.delete(msg.uid);
    }
    handleSTTCommandMessage(msg) {
        var _a;
        const messages = [];
        this.commandFactory.parse(msg, messages);
        if (msg.uid !== undefined) {
            const controller = this.controller.get(msg.uid);
            const selection = this.selectionHandler.getSelection(msg.uid);
            if (controller !== undefined) {
                for (const it of this.availableSpeechCommands) {
                    const handler = this.speechCommandHandler.get(it.command);
                    (_a = handler === null || handler === void 0 ? void 0 : handler.onSpeechCommand) === null || _a === void 0 ? void 0 : _a.call(handler, it, msg.cmd, msg.parameters, controller, selection === null || selection === void 0 ? void 0 : selection.uid, selection === null || selection === void 0 ? void 0 : selection.hitPoint);
                }
            }
        }
    }
    handlePerformActionMessage(msg) {
        const controller = msg.controllerUid !== undefined ? this.controller.get(msg.controllerUid) : undefined;
        void this.performActions(msg.actions, {
            targetUid: msg.targetUid,
            sourceUid: msg.controllerUid,
            position: msg.targetPosition,
            controller
        });
    }
    handleSetControllerButtonActionsMessage(msg) {
        this.controllerButtonActions.set(msg.button, msg.actions);
    }
    handleSetAvailableSpeechCommandsMessage(msg) {
        this.availableSpeechCommands = msg.commands;
    }
    /// Facet handlers ///
    handleInteractionFacet(uid, facet, metaData) {
        this.setInteraction(facet, uid, metaData.timeStamp);
        this.ensureSelectionHighlightFacetForInteraction(uid);
    }
    handleDisableSelectionHighlight(uid, _facet) {
        this.disableSelectionHighlight.set(uid, true);
        for (const controllerUid of this.controller.keys()) {
            const selection = this.selectionHandler.getSelection(controllerUid);
            if ((selection === null || selection === void 0 ? void 0 : selection.uid) === uid) {
                this.connection.send(new SelectEventMessage(controllerUid, uid, false));
            }
        }
    }
    handleSelectionHighlightFacet(uid, facet) {
        this.selectionHighlightEnabled.set(uid, facet.enabled);
        this.selectionHighlightColor.set(uid, facet.color);
        if (this.pendingAutoCreatedSelectionHighlight.has(uid)) {
            this.pendingAutoCreatedSelectionHighlight.delete(uid);
        }
        else {
            this.autoCreatedSelectionHighlight.delete(uid);
        }
        for (const controllerUid of this.controller.keys()) {
            const selection = this.selectionHandler.getSelection(controllerUid);
            if ((selection === null || selection === void 0 ? void 0 : selection.uid) === uid) {
                this.sendSelectionHighlightEvent(controllerUid, uid, this.shouldShowSelectionHighlight(uid));
            }
        }
    }
    /**
     * When we receive a PickupFacet, we create an interaction facet with a pickup action. This is basically just
     * a shorthand that simplifies making models grabbable in Blender.
     */
    handlePickupFacet(uid, facet, metaData) {
        var _a, _b, _c, _d, _e, _f, _g;
        const model = modelPool.getModel(uid);
        if (model === undefined)
            return;
        const parentUid = (_a = model.getParent()) === null || _a === void 0 ? void 0 : _a.getUID();
        if (parentUid === undefined) {
            console.warn("[interaction] Model with PickupFacet has no parent");
            return;
        }
        const staticOffset = (!facet.useOffsetPriorToPickup && facet.useOffset)
            ? {
                position: (_c = (_b = facet.offset) === null || _b === void 0 ? void 0 : _b.position) !== null && _c !== void 0 ? _c : [0, 0, 0],
                orientation: (_e = (_d = facet.offset) === null || _d === void 0 ? void 0 : _d.orientation) !== null && _e !== void 0 ? _e : [0, 0, 0, 1],
                scale: (_g = (_f = facet.offset) === null || _f === void 0 ? void 0 : _f.scale) !== null && _g !== void 0 ? _g : [1, 1, 1],
            }
            : undefined;
        const interactionFacet = new InteractionFacet({
            onClick: [new PickupAction(undefined, // we don't specify a parent because we want to use the controller that selected the model
                {
                    srcModelUid: uid,
                    cloneModel: facet.cloneModel,
                    offset: staticOffset,
                    useOffsetPriorToPickup: facet.useOffsetPriorToPickup,
                    dropOffMode: facet.dropOffMode,
                    dropOffLayers: facet.dropOffLayers,
                    dropOffButton: facet.dropOffButton,
                    keepSelectionActiveInVR: facet.keepSelectionActiveInVR,
                    showDropOffIcon: facet.showDropOffIcon
                })]
        });
        this.connection.send(new UpdateFacetOnModelMessage(uid, interactionFacet));
        this.setInteraction(interactionFacet, uid, metaData.timeStamp);
    }
    /// Remove facet handlers ///
    handleRemoveInteractionFacet(uid, _facet) {
        this.interactions.delete(uid);
        if (this.autoCreatedSelectionHighlight.has(uid) && this.hasSelectionHighlightFacet(uid)) {
            this.connection.send(new RemoveFacetFromModelMessage(uid, SelectionHighlightFacet));
        }
    }
    handleRemoveDisableSelectionHighlight(uid, _facet) {
        this.disableSelectionHighlight.delete(uid);
        for (const controllerId of this.controller.keys()) {
            const selection = this.selectionHandler.getSelection(controllerId);
            if ((selection === null || selection === void 0 ? void 0 : selection.uid) === uid) {
                this.sendSelectionHighlightEvent(controllerId, uid, this.shouldShowSelectionHighlight(uid));
            }
        }
    }
    handleRemoveSelectionHighlightFacet(uid, _facet) {
        this.selectionHighlightEnabled.delete(uid);
        this.selectionHighlightColor.delete(uid);
        this.autoCreatedSelectionHighlight.delete(uid);
        this.pendingAutoCreatedSelectionHighlight.delete(uid);
        for (const controllerId of this.controller.keys()) {
            const selection = this.selectionHandler.getSelection(controllerId);
            if ((selection === null || selection === void 0 ? void 0 : selection.uid) === uid) {
                this.sendSelectionHighlightEvent(controllerId, uid, this.shouldShowSelectionHighlight(uid));
            }
        }
    }
    handleRemoveSubModelMessage(msg, metaData) {
        const interaction = this.interactions.get(msg.uid);
        if ((interaction !== undefined) && interaction.timeStamp < metaData.timeStamp) {
            this.interactions.delete(msg.uid);
        }
        this.disableSelectionHighlight.delete(msg.uid);
        this.selectionHighlightEnabled.delete(msg.uid);
        this.selectionHighlightColor.delete(msg.uid);
        this.autoCreatedSelectionHighlight.delete(msg.uid);
        this.pendingAutoCreatedSelectionHighlight.delete(msg.uid);
        this.modelsWithContextMenuFacets.delete(msg.uid);
        this.modelTransforms.delete(msg.uid);
    }
    handleModelDestroyed(model) {
        this.handleRemoveSubModelMessage(new RemoveSubModelMessage(model.getUID()), {
            source: { workerId: "", moduleId: ModuleId.INTERACTION },
            timeStamp: Number.POSITIVE_INFINITY,
            transport: "local"
        });
    }
    /// Select button handlers ///
    handleControllerSelectButtonClick(controllerUid) {
        const selection = this.selectionHandler.getSelection(controllerUid);
        const controller = this.controller.get(controllerUid);
        if (selection !== undefined && controller !== undefined) {
            this.onSelectButtonClick(selection.uid, controller, selection.hitPoint);
        }
        else {
            this.onSelectNothingHit.invoke();
            this.connection.send(new NoModelClickedMessage());
        }
    }
    handleControllerSelectButtonDoubleClick(controllerUid) {
        const selection = this.selectionHandler.getSelection(controllerUid);
        const controller = this.controller.get(controllerUid);
        if (selection !== undefined && controller !== undefined) {
            void this.onSelectButtonDoubleClick(selection.uid, controller, selection.hitPoint);
        }
        if (xrService.isPresenting() && this.controllerHandTracking.get(controllerUid) === true) {
            this.connection.send(new ControllerButtonMessage(controllerUid, ButtonType.DISCARD, ButtonSignal.CLICKED, Date.now()));
        }
    }
    handleControllerSelectButtonPress(controllerUid) {
        const selection = this.selectionHandler.getSelection(controllerUid);
        const controller = this.controller.get(controllerUid);
        if (selection !== undefined && controller !== undefined) {
            this.onSelectButtonPress(selection.uid, controller, selection.hitPoint);
        }
    }
    handleControllerSelectButtonRelease(controllerUid) {
        const selection = this.selectionHandler.getSelection(controllerUid);
        const controller = this.controller.get(controllerUid);
        if (selection !== undefined && controller !== undefined) {
            this.onSelectButtonRelease(selection.uid, controller, selection.hitPoint);
        }
    }
    handleControllerSelectButtonHold(controllerUid) {
        const selection = this.selectionHandler.getSelection(controllerUid);
        const controller = this.controller.get(controllerUid);
        if (selection !== undefined && controller !== undefined) {
            this.onSelectButtonHold(selection.uid, controller, selection.hitPoint);
        }
    }
    handleControllerSelectButtonHoldReleased(controllerUid) {
        const controller = this.controller.get(controllerUid);
        if (controller !== undefined) {
            const selection = this.selectionHandler.getSelection(controllerUid);
            if (selection !== undefined) {
                this.onSelectButtonHoldReleased(selection.uid, controller, selection.hitPoint);
            }
        }
    }
    /// Selection highlight ///
    shouldShowSelectionHighlight(uid) {
        var _a;
        if (this.disableSelectionHighlight.has(uid))
            return false;
        const highlightFacet = (_a = modelPool.getModel(uid)) === null || _a === void 0 ? void 0 : _a.getFacet(SelectionHighlightFacet);
        if (highlightFacet !== undefined)
            return highlightFacet.enabled;
        return false;
    }
    hasSelectionHighlightFacet(uid) {
        return this.selectionHighlightEnabled.has(uid);
    }
    ensureSelectionHighlightFacetForInteraction(uid) {
        if (this.hasSelectionHighlightFacet(uid) || this.pendingAutoCreatedSelectionHighlight.has(uid)) {
            return;
        }
        this.autoCreatedSelectionHighlight.add(uid);
        this.pendingAutoCreatedSelectionHighlight.add(uid);
        this.connection.send(new UpdateFacetOnModelMessage(uid, new SelectionHighlightFacet()));
    }
    sendSelectionHighlightEvent(controllerUid, uid, isSelected) {
        var _a, _b, _c;
        const color = isSelected
            ? (_c = (_b = (_a = modelPool.getModel(uid)) === null || _a === void 0 ? void 0 : _a.getFacet(SelectionHighlightFacet)) === null || _b === void 0 ? void 0 : _b.color) !== null && _c !== void 0 ? _c : this.selectionHighlightColor.get(uid)
            : this.selectionHighlightColor.get(uid);
        this.connection.send(new SelectEventMessage(controllerUid, uid, isSelected, color));
    }
    /// Private helper methods ///
    setInteraction(facet, uid, timeStamp) {
        this.interactions.set(uid, { facet, timeStamp });
    }
    forBehaviorOfModel(uid, callback) {
        const interaction = this.interactions.get(uid);
        if ((interaction === null || interaction === void 0 ? void 0 : interaction.facet.behaviors) !== undefined) {
            for (const param of interaction.facet.behaviors) {
                const handler = this.behaviorHandler.get(param.behavior);
                if (handler !== undefined)
                    callback(param, handler);
            }
        }
    }
    performActions(actions, context) {
        return __awaiter(this, void 0, void 0, function* () {
            if ((actions !== undefined) && actions.length > 0) {
                for (const action of actions) {
                    const handler = this.actionHandler.get(action.action);
                    if (handler !== undefined) {
                        yield handler.performAction(action, context);
                    }
                    else {
                        this.connection.send(new PerformServerActionMessage(action, context.targetUid, context.position));
                    }
                }
            }
        });
    }
}
__decorate([
    MessageHandler(EndSessionMessage)
], InteractionModule.prototype, "onReset", null);
__decorate([
    MessageHandler(ResetControllerButtonActionsMessage)
], InteractionModule.prototype, "resetControllerButtonActions", null);
__decorate([
    ModelEventHandler(ModelEventType.TRANSFORM_UPDATED)
], InteractionModule.prototype, "handleModelTransformUpdateMessage", null);
__decorate([
    MessageHandler(InteractionFacetActivationMessage)
], InteractionModule.prototype, "handleInteractionFacetActivationMessage", null);
__decorate([
    MessageHandler(ControllerButtonMessage)
], InteractionModule.prototype, "handleControllerButtonMessage", null);
__decorate([
    MessageHandler(RequestModelSelectionMessage)
], InteractionModule.prototype, "handleRequestModelSelectionMessage", null);
__decorate([
    MessageHandler(ControllerMovedMessage)
], InteractionModule.prototype, "handleControllerMovedMessage", null);
__decorate([
    MessageHandler(ControllerConnectedMessage)
], InteractionModule.prototype, "handleControllerConnectedMessage", null);
__decorate([
    MessageHandler(ControllerTrackingChangedMessage)
], InteractionModule.prototype, "handleControllerTrackingChangedMessage", null);
__decorate([
    MessageHandler(ControllerDisconnectedMessage)
], InteractionModule.prototype, "handleControllerDisconnectedMessage", null);
__decorate([
    MessageHandler(STTCommandMessage)
], InteractionModule.prototype, "handleSTTCommandMessage", null);
__decorate([
    MessageHandler(PerformActionsMessage)
], InteractionModule.prototype, "handlePerformActionMessage", null);
__decorate([
    MessageHandler(SetControllerButtonActionsMessage)
], InteractionModule.prototype, "handleSetControllerButtonActionsMessage", null);
__decorate([
    MessageHandler(SetAvailableSpeechCommandsMessage)
], InteractionModule.prototype, "handleSetAvailableSpeechCommandsMessage", null);
__decorate([
    FacetHandler(InteractionFacet)
], InteractionModule.prototype, "handleInteractionFacet", null);
__decorate([
    FacetHandler(DisableSelectionHighlight)
], InteractionModule.prototype, "handleDisableSelectionHighlight", null);
__decorate([
    FacetHandler(SelectionHighlightFacet)
], InteractionModule.prototype, "handleSelectionHighlightFacet", null);
__decorate([
    FacetHandler(PickupFacet)
], InteractionModule.prototype, "handlePickupFacet", null);
__decorate([
    FacetRemoveHandler(InteractionFacet)
], InteractionModule.prototype, "handleRemoveInteractionFacet", null);
__decorate([
    FacetRemoveHandler(DisableSelectionHighlight)
], InteractionModule.prototype, "handleRemoveDisableSelectionHighlight", null);
__decorate([
    FacetRemoveHandler(SelectionHighlightFacet)
], InteractionModule.prototype, "handleRemoveSelectionHighlightFacet", null);
__decorate([
    MessageHandler(DeleteModelMessage, RemoveModelMessage, RemoveSubModelMessage)
], InteractionModule.prototype, "handleRemoveSubModelMessage", null);
__decorate([
    ModelEventHandler(ModelEventType.MODEL_DESTROYED)
], InteractionModule.prototype, "handleModelDestroyed", null);
